import React, { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { GetServerSideProps, InferGetServerSidePropsType, NextPage } from 'next'
import { Button } from '@awaze/design-system.components.button'
import { Heading, Text } from '@awaze/design-system.components.text'
import { Box } from '@awaze/design-system.components.box'
import { Stack } from '@awaze/design-system.components.stack'
import {
  Label,
  InputControl,
  InputDescription,
  Input,
} from '@awaze/design-system.components.input'
import { useForm } from '@awaze/design-system.components.form'

import {
  ELEMENT_GROUP_SPACING,
  MAIN_HEADING_SIZES,
  LOCAL_STORAGE_RETURN_URL_KEY,
} from '../../src/lib/constants'
import {
  EmailInputField,
  PasswordInputWithValidation,
} from '../../src/components/inputFields'
import TermsOfService from '../../src/components/termsOfService/termsOfService'
import { signUpMockedData } from '../../mocks/contentful/signUpMockedData'
import {
  selectBrand,
  selectSetEmail,
  selectLang,
  selectSetBanner,
} from '../../src/store/selectors'
import { Loader } from '../../src/components/loader'
import { CustomHead } from '../../src/components/custom-head/custom-head'
import { ContentfulData, getContentfulData } from '../../utils/contentful'
import { parseToSnakeCase } from '../../utils/general'
import { usePBStore } from '../../src/store/provider'
import { SignUpErrors } from '../../src/types/signUpErrors'
import { Checkbox } from '@awaze/design-system.components.checkbox'
import styled from 'styled-components'
import { ButtonLinkStyled } from '../../src/components/buttonLinkStyled/buttonLinkStyled'
import { getQueryParamAsString } from '@/lib/query-param-util'

type SignUpFormFields = {
  emailAddress: string
  password: string
  firstName: string
  lastName: string
}

const StyledLabel = styled(Label)`
  margin-bottom: 8px;
`

const Index: NextPage<
  InferGetServerSidePropsType<typeof getServerSideProps>
> = ({ signUpData, host, returnUrl }) => {
  const router = useRouter()
  const formHooks = useForm<SignUpFormFields>()
  const setBanner = usePBStore(selectSetBanner)
  const brand = usePBStore(selectBrand)
  const locale = usePBStore(selectLang)
  const setEmail = usePBStore(selectSetEmail)
  const [isMarketingOptedIn, setIsMarketingOptedIn] = useState(false)
  const [loading, setLoading] = useState(false)
  const { inputs, labels, links } = signUpData

  useEffect(() => {
    // if a return url is provided in the url query, store it in local storage
    // that way it can be retrieved when the user logs in
    if (returnUrl) {
      try {
        localStorage?.setItem(LOCAL_STORAGE_RETURN_URL_KEY, returnUrl)
      } catch (e) {
        // Local storage not available
      }
    }
  }, [returnUrl])

  const onSubmit = async () => {
    setLoading(true)
    const lang = parseToSnakeCase(locale)
    const userDetails = formHooks.getValues()

    const marketingPreferences = {
      noEmail: !isMarketingOptedIn,
      noSMS: !isMarketingOptedIn,
      noPost: !isMarketingOptedIn,
      noPhone: !isMarketingOptedIn,
    }

    const payload = {
      email: userDetails.emailAddress,
      password: userDetails.password,
      firstName: userDetails.firstName,
      lastName: userDetails.lastName,
      ...marketingPreferences,
    }

    const response = await fetch(`../api/signup-route?lang=${lang}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-awaze-brand': `${brand}`,
      },
      body: JSON.stringify(payload),
    })

    const data = await response.json()

    if (response.status < 200 || response.status > 299) {
      switch (data.error) {
        case SignUpErrors.UserAlreadyExist:
          setBanner(
            'UserAlreadyExist',
            labels['label_user_already_exists'],
            'login',
          )
          break
        case SignUpErrors.EmailFormattingError:
          setBanner(
            'EmailFormattingError',
            labels['label_email_formatting_error'],
          )
          break
        default:
          setBanner(
            'UnknownErrorHappened',
            labels['label_unknown_error_happened'],
          )
      }
    } else {
      if (
        (brand === 'novasol' || (brand as string) === 'jamesVillas') &&
        isMarketingOptedIn
      ) {
        try {
          await fetch(`/api/landing-pages/public/${brand}/newsletter-signup`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              host,
            },
            body: JSON.stringify({
              emailaddress: userDetails.emailAddress,
              firstname: userDetails.firstName,
              lastname: userDetails.lastName,
            }),
          })
        } catch (error) {
          console.error('Error sending marketing preferences signup:', error)
        }
      }
      setEmail(userDetails.emailAddress)
      router.push({ pathname: '/sign-up/confirmation' })
    }

    setLoading(false)
  }
  return (
    <Box>
      <CustomHead
        brand={brand}
        pageName={labels['seo_page_name'] ?? ''}
        content={labels['seo_page_description'] ?? ''}
      />

      <form onSubmit={formHooks.handleSubmit(onSubmit)}>
        <Stack spacing={ELEMENT_GROUP_SPACING}>
          {/* A L E R T  B A N N E R S */}

          <Stack isHorizontal align="center" justify="space-between">
            <Heading sizes={MAIN_HEADING_SIZES}>
              {labels['label_signup']}
            </Heading>
          </Stack>

          {/* F I R S T  N A M E */}

          <InputControl>
            <Label htmlFor="firstName">
              {inputs['input_first_name']?.label}
            </Label>
            <Input
              name="firstName"
              type="text"
              // @ts-expect-error because of the way the formhooks is typed by the Input component from the design system
              formHooks={formHooks}
              validation={{
                required: inputs['input_first_name']?.required,
                pattern: {
                  message: inputs['input_first_name']?.message,
                },
              }}
              placeholder={inputs['input_first_name']?.placeholder}
            />
          </InputControl>

          {/* L A S T  N A M E */}

          <InputControl>
            <Label htmlFor="lastName">{inputs['input_last_name']?.label}</Label>
            <Input
              name="lastName"
              // @ts-expect-error because of the way the formhooks is typed by the Input component from the design system
              formHooks={formHooks}
              validation={{
                required: inputs['input_last_name']?.required,
                pattern: {
                  message: inputs['input_last_name']?.message,
                },
              }}
              placeholder={inputs['input_last_name']?.placeholder}
            />
          </InputControl>

          {/* E M A I L  */}

          <InputControl>
            <Label htmlFor="emailAddress">{inputs['input_email']?.label}</Label>
            <EmailInputField
              formHooks={formHooks}
              validation={{
                required: inputs['input_email']?.required,
                pattern: {
                  value: /(.+)@.+\..+/,
                  message: inputs['input_email']?.message,
                },
              }}
              placeholder={inputs['input_email']?.placeholder}
            />
          </InputControl>

          {/* P A S S W O R D */}

          <InputControl>
            <StyledLabel htmlFor="password">
              {inputs['input_create_pass']?.label}
            </StyledLabel>

            <InputDescription>
              {inputs['input_create_pass']?.description}
            </InputDescription>

            <PasswordInputWithValidation
              formHooks={formHooks}
              requiredMessage={inputs['input_create_pass']?.required}
              minLengthMessage={inputs['input_create_pass']?.message}
              placeholder={inputs['input_create_pass']?.placeholder}
              iconPlaceholder={inputs['input_create_pass']?.iconTitle}
            />
          </InputControl>

          {/* M A R K E T I N G  P R E F E R E N C E S */}

          <Stack direction="column" spacing={8} wrap="wrap" fullWidth>
            <Text sizes={100} type="bold">
              {labels['label_marketing_preferences'] ?? ''}
            </Text>
            <Checkbox
              checked={isMarketingOptedIn}
              id="checkbox"
              name="marketing_preferences_checkbox"
              onChange={() => setIsMarketingOptedIn((prev) => !prev)}
              value="checkbox"
              align="top"
              label={
                <Text sizes={100} as="span">
                  {labels['label_marketing_preferences_checkbox']}
                </Text>
              }
            />
          </Stack>

          {/* R E G I S T E R  B U T T O N */}
          <Button
            icon={loading ? <Loader /> : undefined}
            text={
              loading
                ? ''
                : (labels['button_create_account'] ?? 'Create account')
            }
            type="submit"
            colourScheme="brand-primary"
            data-testid="create-account-button"
          />

          {/* O T H E R   L O G I N */}

          <Stack align="center">
            <Text sizes={300} as="span">
              {labels['label_have_account']}{' '}
              <ButtonLinkStyled
                text={links['link_login']?.title ?? 'Log in'}
                type="button"
                onClick={() => {
                  router.push('/login')
                }}
                data-testid="login-button"
                colourScheme="brand-secondary"
                variant="text"
              />
            </Text>
          </Stack>

          {/* T E R M S  O F  S E R V I CE */}

          <TermsOfService data={signUpData} />
        </Stack>
      </form>
    </Box>
  )
}

type PageProps = {
  signUpData: ContentfulData
  host: string
  returnUrl: string | null
}

export const getServerSideProps: GetServerSideProps<PageProps> = async ({
  locale = 'en-GB',
  req,
  query,
}) => {
  const { returnurl, returnUrl } = query ?? {}
  // we support both returnurl and returnUrl for ease of use
  const parsedReturnUrl =
    getQueryParamAsString(returnurl) ?? getQueryParamAsString(returnUrl)
  const brand = req?.headers['x-awaze-brand'] ?? 'awaze'
  const host =
    (req?.headers?.['x-original-hostname'] as string) ??
    (req?.headers?.['x-forwarded-host'] as string) ??
    req.headers.host ??
    ''

  const signUpData = await getContentfulData(
    brand,
    locale,
    '/sign-up',
    signUpMockedData,
  )

  return {
    props: {
      signUpData,
      host,
      returnUrl: parsedReturnUrl,
    },
  }
}

export default Index
